import Button from 'components/Button';
import DjangoMessages from 'components/DjangoMessages';
import FormResult from 'components/Form/FormResult';
import Information from 'components/Information';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import TablePagination from 'components/Table/TableComponents/TablePagination';
import Table from 'components/Table/TableStatic';
import Truncator from 'components/Truncator';
import {FC} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useBackend} from 'state/backend';
import {gettext, t} from 'ttag';
import {jstDateTimeString} from 'utils/date';
import updateUrlParamsAndReload from 'utils/pagination';
import {setUrl} from 'utils/request';
import {onApiError, onApiSuccess} from 'utils/request-axios/utils';

import ExportHistoryDataModal, {
    useExportHistoryDataModal,
} from '../components/ExportModal';
import Filter from './components/filter';
import {useUndelete} from './services';
import {EventsBackendData, FilterFormData} from './types';

const Events: FC = () => {
    const {
        appPermissions,
        data: {defaultFilteredValues, eventList, isFiltered, pagination},
    } = useBackend<EventsBackendData>();

    const modalProps = useExportHistoryDataModal('history_entries');

    const methods = useForm<FilterFormData>({
        defaultValues: {
            action_flag: defaultFilteredValues.action,
            created_by: defaultFilteredValues.member,
            date_begin: defaultFilteredValues.dateFrom,
            date_end: defaultFilteredValues.dateTo,
            object_repr: defaultFilteredValues.itemName,
        },
    });

    const apiUndelete = useUndelete();

    const handleUndelete = async (url: string) => {
        await apiUndelete
            .mutateAsync({url})
            .then(
                onApiSuccess({
                    onSuccess: (response) => {
                        if (response.name === 'document_item_view_redirect') {
                            return setUrl(
                                window.Urls.document_item_view(
                                    response.data.mid,
                                ),
                            );
                        }

                        if (response.name === 'client_summary_view_redirect') {
                            return setUrl(
                                window.Urls.client_summary_view(
                                    response.data.mid,
                                ),
                            );
                        }

                        if (response.name === 'preset_item_view_redirect') {
                            return setUrl(
                                window.Urls.preset_item_view(response.data.mid),
                            );
                        }
                        throw new Error('Unknown response');
                    },
                }),
            )
            .catch(onApiError(methods));
    };

    const noEventsInformationMessage = isFiltered ? (
        <Information>{t`No events found. Try changing your search terms.`}</Information>
    ) : (
        <Information>{t`No actions have been recorded for your company.`}</Information>
    );

    return (
        <FormProvider {...methods}>
            <FormResult />
            <div className="flex flex-col gap-2">
                <DjangoMessages />
                <NavigationTabs.History
                    permissions={appPermissions}
                    selection="events"
                />
                <Filter />
                {eventList.length === 0 ? (
                    noEventsInformationMessage
                ) : (
                    <>
                        <Table classNameTable="table-fixed" hasStripes={true}>
                            <Table.Header className="text-nowrap">
                                <Table.HeaderRow>
                                    <Table.Column className="w-44">{t`Date & Time`}</Table.Column>
                                    <Table.Column className="w-36">{t`Member`}</Table.Column>
                                    <Table.Column className="w-44">{t`Action`}</Table.Column>
                                    <Table.Column className="flex items-center justify-between gap-2">
                                        <span>{t`Item Name`}</span>
                                        <Button
                                            className="font-normal"
                                            kind="secondary"
                                            onClick={() =>
                                                modalProps.setIsModalOpen(true)
                                            }
                                            size="xs"
                                        >{t`Export`}</Button>
                                    </Table.Column>
                                </Table.HeaderRow>
                            </Table.Header>
                            <Table.Body>
                                {eventList.map(
                                    (event) =>
                                        event && (
                                            <Table.Row key={event.mid}>
                                                <Table.Cell className="break-words">
                                                    {jstDateTimeString(
                                                        event.dateAndTime,
                                                    )}
                                                </Table.Cell>
                                                <Table.Cell className="break-words">
                                                    {event.member}
                                                </Table.Cell>
                                                <Table.Cell className="break-words">
                                                    {event.action}
                                                </Table.Cell>
                                                <Table.Cell>
                                                    {event.hasNoLinkToDisplay ? (
                                                        <Truncator
                                                            text={`${event.label} ${event.url === '' && event.isObjectDeleted ? gettext('(Deleted)') : ''}`}
                                                            truncateAt="middle"
                                                        />
                                                    ) : (
                                                        <Truncator
                                                            href={event.url}
                                                            text={event.label}
                                                            truncateAt="middle"
                                                        />
                                                    )}
                                                    {event.isUndoable && (
                                                        <Button
                                                            kind="secondary"
                                                            onClick={() =>
                                                                handleUndelete(
                                                                    event.editedObjectUrl,
                                                                )
                                                            }
                                                            size="xs"
                                                        >{t`Undelete`}</Button>
                                                    )}
                                                </Table.Cell>
                                            </Table.Row>
                                        ),
                                )}
                            </Table.Body>
                        </Table>
                        <TablePagination
                            className="mb-5"
                            hideForSinglePage={true}
                            id="groups-pagination"
                            isAmbigousStop={true}
                            onChange={(page) =>
                                updateUrlParamsAndReload('page', page)
                            }
                            onFirst={() => updateUrlParamsAndReload('page', 1)}
                            onLast={() =>
                                updateUrlParamsAndReload('page', 'last')
                            }
                            pageCurrent={pagination.currentPage}
                            pageStop={pagination.numPages}
                            paginationPadding={pagination.windowPages}
                        />
                    </>
                )}
            </div>
            <ExportHistoryDataModal {...modalProps} />
        </FormProvider>
    );
};
export default Events;
